import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CardActions from '@material-ui/core/CardActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Button from 'components/StyledButton/StyledButton';

import FlexPusher from '../../../../common/styled/FlexPusher/FlexPusher';
import { ReduxState } from '../../../../redux/types';
import Document from '../../../../types/Document';
import api from '../../../../utils/api';
import { dateToLocale } from '../../../../utils/date';
import { useDialog } from '../../../../utils/dialog';
import { downloadFile } from '../../../../utils/download';
import snackbar from '../../../../utils/snackbar';
import { CentralButtonContainer, FlexContainer, LeftButtonContainer, RightButtonContainer, StyledCard, StyledCardContent, StyledChip, StyledFileIcon, StyledFlag, StyledIconButton, StyledTypography, UserNameTypography } from './styled';

interface Props {
    document: Document;
    openEditDialog: () => void;
    updateData: () => void;
    basePath?: string;
    publicPage?: boolean;
    isAdmin?: boolean;
}

const DocumentCard = (props: Props) => {
    const { document, openEditDialog, updateData, basePath, publicPage, isAdmin } = props;
    const { creationDate, companyId, documentName, extension, id, note, userName, userId } = document;

    const { t } = useTranslation();

    const languageCode = useMemo(() => {
        return t('language_code');
    }, [t]);

    const userInfo = useSelector((state: ReduxState) => state.user);
    const loggedUserId = userInfo.id;

    const [isLoading, setIsLoading] = useState(false);
    const [openDialog, closeDialog] = useDialog();

    const showFullNote = useCallback(() => {
        openDialog({
            title: documentName,
            message: note ?? ''
        });
    }, [documentName, note, openDialog]);

    const base64toBlob = useCallback((dataURI: string) => {
        // convert a base64 string to a blob object
        const byteString = atob(dataURI);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ab]);
    }, []);

    const download = useCallback(() => {
        // fetch the file content, decode the base64 and download it
        setIsLoading(true);
        api.request((basePath ? (basePath + '/') : (('/companies/' + companyId + '/documents/'))) + id).then(res => {
            const { content } = res;
            const fileName = documentName.replace(/\W+/g, '_');
            const blob = base64toBlob(content);

            downloadFile(blob, fileName + '.' + extension);

            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    }, [basePath, base64toBlob, companyId, documentName, id, extension]);

    const deleteDocument = useCallback(() => {
        api.request((basePath ? (basePath + '/') : (('/companies/' + companyId + '/documents/'))) + id, 'DELETE').then(() => {
            snackbar.success(t('dialogs.document_delete.success'));
            updateData();
        });
    }, [basePath, companyId, id, updateData, t]);

    const showDeleteDialog = useCallback(() => {
        openDialog({
            title: t('dialogs.document_delete.title'),
            message: t('dialogs.document_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteDocument();
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteDocument, openDialog, t]);

    // trim note to 200 characters
    const noteAbstract = (note && note.length > 200) ? (note.substr(0, 200).trim() + '...') : note;
    const showReadMore = note && noteAbstract && note.length !== noteAbstract.length;

    const date = dateToLocale(creationDate);

    const isLoggedUserOwner = loggedUserId === userId;

    return (
        <StyledCard>
            <StyledCardContent>
                <StyledFileIcon name={'file.' + document.extension} />

                <FlexContainer>
                    <StyledTypography gutterBottom >
                        {documentName}
                    </StyledTypography>

                    <FlexPusher />

                    {!publicPage && (
                        <StyledChip label={t('models.document.enum.category_short.' + document.category)} variant='outlined' />
                    )}

                    {document.language && (isAdmin || languageCode !== document.language.code) && (
                        <div style={{ width: '40px', flexShrink: 0, textAlign: 'right' }}>
                            <StyledFlag code={document.language.code} />
                        </div>
                    )}
                </FlexContainer>

                <StyledTypography variant='body2' gutterBottom >
                    {noteAbstract} {showReadMore && (
                        <Link onClick={showFullNote} href='#'>{t('pages.documents.read_more')}</Link>
                    )}
                </StyledTypography>

                <FlexPusher />

                <UserNameTypography variant='body2' gutterBottom >
                    {userName}
                </UserNameTypography>

                <StyledTypography variant='body2' color='textSecondary' >
                    {date}
                </StyledTypography>
            </StyledCardContent>

            <CardActions>
                {(isLoggedUserOwner || (publicPage && isAdmin)) && (
                    <LeftButtonContainer>
                        <StyledIconButton onClick={openEditDialog} disabled={isLoading} size='small' >
                            <EditIcon />
                        </StyledIconButton>
                    </LeftButtonContainer>
                )}

                <CentralButtonContainer>
                    {isLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Button size='small' onClick={download} disabled={isLoading} >
                            {t('pages.documents.' + (document.extension === 'pdf' ? 'preview' : 'download'))}
                        </Button>
                    )}
                </CentralButtonContainer>

                {(isLoggedUserOwner || (publicPage && isAdmin)) && (
                    <RightButtonContainer>
                        <StyledIconButton onClick={showDeleteDialog} disabled={isLoading} size='small' >
                            <DeleteIcon />
                        </StyledIconButton>
                    </RightButtonContainer>
                )}
            </CardActions>
        </StyledCard>
    );
};

export default DocumentCard;
